import type {
  DeckCardRef,
  DeckDraft,
  DeckMatchup,
  DeckRune,
  DeckVersion,
  DeckVideo,
} from '../types/deck';
import { isChampionUnitCard } from './championIdentity';
import { metafyArticlesEqual } from './metafyArticle';

export interface CardChange {
  card: DeckCardRef;
  zone: string;
  delta: number;
}

export interface DiffResult {
  summary: string;
  added: CardChange[];
  removed: CardChange[];
  guideChanged: boolean;
  videoChanged: boolean;
  metafyChanged: boolean;
}

function diffZone(
  nextCards: DeckCardRef[],
  prevCards: DeckCardRef[],
  zoneName: string,
  added: CardChange[],
  removed: CardChange[],
): void {
  const prevMap = new Map(prevCards.map((c) => [c.cardId, c]));
  const nextMap = new Map(nextCards.map((c) => [c.cardId, c]));

  for (const [, nextCard] of nextMap) {
    const prevQty = prevMap.get(nextCard.cardId)?.qty ?? 0;
    const delta = nextCard.qty - prevQty;
    if (delta > 0) added.push({ card: { ...nextCard, qty: delta }, zone: zoneName, delta });
  }
  for (const [, prevCard] of prevMap) {
    const nextQty = nextMap.get(prevCard.cardId)?.qty ?? 0;
    const delta = prevCard.qty - nextQty;
    if (delta > 0) removed.push({ card: { ...prevCard, qty: delta }, zone: zoneName, delta });
  }
}

function cardQtyMap(cards: DeckCardRef[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const c of cards) {
    m.set(c.cardId, (m.get(c.cardId) ?? 0) + c.qty);
  }
  return m;
}

function cardListsEqual(a: DeckCardRef[], b: DeckCardRef[]): boolean {
  const ma = cardQtyMap(a);
  const mb = cardQtyMap(b);
  if (ma.size !== mb.size) return false;
  for (const [id, qty] of ma) {
    if (mb.get(id) !== qty) return false;
  }
  return true;
}

function runeListsEqual(a: DeckRune[], b: DeckRune[]): boolean {
  const toMap = (runes: DeckRune[]) => {
    const m = new Map<string, number>();
    for (const r of runes) {
      m.set(r.domain, (m.get(r.domain) ?? 0) + r.qty);
    }
    return m;
  };
  const ma = toMap(a);
  const mb = toMap(b);
  if (ma.size !== mb.size) return false;
  for (const [domain, qty] of ma) {
    if (mb.get(domain) !== qty) return false;
  }
  return true;
}

function cardSlotEqual(a?: DeckCardRef, b?: DeckCardRef): boolean {
  return (a?.cardId ?? '') === (b?.cardId ?? '');
}

function videoEqual(a?: DeckVideo, b?: DeckVideo): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.url === b.url && (a.title ?? '') === (b.title ?? '');
}

function sortedStringArrayEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

function matchupsEqual(a: DeckMatchup[], b: DeckMatchup[]): boolean {
  const key = (m: DeckMatchup) => `${m.archetype}\0${m.result}\0${m.notes}`;
  const sa = [...a].map(key).sort();
  const sb = [...b].map(key).sort();
  return sa.length === sb.length && sa.every((v, i) => v === sb[i]);
}

/** True when cards and deck metadata match (ignores validation-derived fields). */
export function deckDraftUnchanged(prev: DeckDraft, next: DeckDraft): boolean {
  return (
    prev.title === next.title &&
    prev.description === next.description &&
    prev.guide === next.guide &&
    videoEqual(prev.video, next.video) &&
    metafyArticlesEqual(prev.metafy, next.metafy) &&
    sortedStringArrayEqual(prev.playstyle, next.playstyle) &&
    prev.formatCode === next.formatCode &&
    sortedStringArrayEqual(prev.tags, next.tags) &&
    (prev.coverCardId ?? '') === (next.coverCardId ?? '') &&
    matchupsEqual(prev.matchups, next.matchups) &&
    cardSlotEqual(prev.legend, next.legend) &&
    cardListsEqual(prev.chosenLegends ?? [], next.chosenLegends ?? []) &&
    cardSlotEqual(prev.champion, next.champion) &&
    cardListsEqual(prev.battlefields, next.battlefields) &&
    cardListsEqual(prev.mainDeck, next.mainDeck) &&
    cardListsEqual(prev.sideboard, next.sideboard) &&
    cardListsEqual(prev.considered ?? [], next.considered ?? []) &&
    runeListsEqual(prev.runes, next.runes) &&
    prev.status === next.status &&
    prev.visibility === next.visibility
  );
}

export function computeVersionDiff(
  next: DeckVersion['snapshot'],
  prev: DeckVersion['snapshot'] | null,
): DiffResult {
  const added: CardChange[] = [];
  const removed: CardChange[] = [];

  diffZone(
    (next.mainDeck ?? []).filter(isChampionUnitCard),
    (prev?.mainDeck ?? []).filter(isChampionUnitCard),
    'Champions', added, removed,
  );
  diffZone(
    (next.mainDeck ?? []).filter((c) => !isChampionUnitCard(c)),
    (prev?.mainDeck ?? []).filter((c) => !isChampionUnitCard(c)),
    'Main Deck', added, removed,
  );
  diffZone(next.sideboard ?? [], prev?.sideboard ?? [], 'Sideboard', added, removed);
  diffZone(next.considered ?? [], prev?.considered ?? [], 'Considered', added, removed);
  diffZone(next.battlefields ?? [], prev?.battlefields ?? [], 'Battlefields', added, removed);

  if ((next.champion?.cardId ?? '') !== (prev?.champion?.cardId ?? '')) {
    if (next.champion) added.push({ card: { ...next.champion, qty: 1 }, zone: 'Champion Slot', delta: 1 });
    if (prev?.champion) removed.push({ card: { ...prev.champion, qty: 1 }, zone: 'Champion Slot', delta: 1 });
  }

  if ((next.legend?.cardId ?? '') !== (prev?.legend?.cardId ?? '')) {
    if (next.legend) added.push({ card: { ...next.legend, qty: 1 }, zone: 'Legend', delta: 1 });
    if (prev?.legend) removed.push({ card: { ...prev.legend, qty: 1 }, zone: 'Legend', delta: 1 });
  }

  // Older snapshots predate the field.
  diffZone(next.chosenLegends ?? [], prev?.chosenLegends ?? [], 'Chosen legends', added, removed);

  const sum = (list: CardChange[], zone: string) =>
    list.filter((c) => c.zone === zone).reduce((s, c) => s + c.delta, 0);

  const parts: string[] = [];
  const cA = sum(added, 'Champions'),   cR = sum(removed, 'Champions');
  const mA = sum(added, 'Main Deck'),   mR = sum(removed, 'Main Deck');
  const sA = sum(added, 'Sideboard'),   sR = sum(removed, 'Sideboard');
  const kA = sum(added, 'Considered'),  kR = sum(removed, 'Considered');
  const bA = sum(added, 'Battlefields'), bR = sum(removed, 'Battlefields');

  if (cA > 0 || cR > 0) parts.push(`Champs: +${cA}, -${cR}`);
  if (mA > 0 || mR > 0) parts.push(`Main: +${mA}, -${mR}`);
  if (sA > 0 || sR > 0) parts.push(`Sideboard: +${sA}, -${sR}`);
  if (kA > 0 || kR > 0) parts.push(`Considered: +${kA}, -${kR}`);
  if (bA > 0 || bR > 0) parts.push(`Battlefields: +${bA}, -${bR}`);
  if (sum(added, 'Champion Slot') + sum(removed, 'Champion Slot') > 0) parts.push('Champion Slot changed');
  if (sum(added, 'Legend') + sum(removed, 'Legend') > 0) parts.push('Legend changed');
  if (sum(added, 'Chosen legends') + sum(removed, 'Chosen legends') > 0) parts.push('Chosen legends changed');

  const guideChanged = (next.guide ?? '') !== (prev?.guide ?? '');
  const videoChanged = !videoEqual(next.video, prev?.video);
  const metafyChanged = !metafyArticlesEqual(next.metafy, prev?.metafy);

  if (guideChanged) parts.push('Guide updated');
  if (videoChanged) parts.push('Video updated');
  if (metafyChanged) parts.push('Metafy article updated');

  return {
    summary: parts.length ? parts.join(' | ') : 'No changes',
    added,
    removed,
    guideChanged,
    videoChanged,
    metafyChanged,
  };
}
